import type { FC, ReactNode } from 'react';

interface SkeletonProps {
  /** Tailwind size classes, e.g. "h-4 w-1/3". */
  className?: string;
}

/**
 * One placeholder block. Cards render these in the shape of the content that
 * is coming, so a loading card looks like a card, not a frozen page with a
 * line of grey text. Wrap a group in <SkeletonGroup> for the a11y status.
 */
export const Skeleton: FC<SkeletonProps> = ({ className = 'h-4 w-full' }) => (
  <div aria-hidden="true" className={`animate-pulse rounded-md bg-white/10 ${className}`} />
);

interface SkeletonGroupProps {
  label: string;
  className?: string;
  testId?: string;
  children: ReactNode;
}

/** Announces "loading" once for the group instead of per block. */
export const SkeletonGroup: FC<SkeletonGroupProps> = ({
  label,
  className,
  testId = 'skeleton',
  children,
}) => (
  <div role="status" aria-busy="true" aria-label={label} data-testid={testId} className={className}>
    {children}
    <span className="sr-only">{label}</span>
  </div>
);

/** Three metric tiles, the shape EnforcementHealthCard and UsageSection render. */
export const SkeletonTiles: FC = () => (
  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
    {[0, 1, 2].map((i) => (
      <div key={i} className="rounded-lg border border-white/10 bg-black/30 px-4 py-3">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="mt-3 h-7 w-16" />
      </div>
    ))}
  </div>
);
